import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiFetch } from '../../api';
import { COUNTRY_ISO } from '../../constants';

const LIMIT = 100;

function formatDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

function formatValue(val, band) {
  if (val) return '€' + Number(val).toLocaleString();
  if (band && band !== 'unknown') return band.charAt(0).toUpperCase() + band.slice(1);
  return '—';
}

function isExpired(deadline) {
  if (!deadline) return false;
  return new Date(deadline) < new Date();
}

const s = {
  page: { maxWidth: 1140, margin: '32px auto', padding: '0 24px 60px' },
  heading: { fontSize: 24, fontWeight: 700, color: '#0f172a', marginBottom: 6 },
  sub: { fontSize: 14, color: '#64748b', marginBottom: 24 },

  statsBar: { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 20 },
  statCard: { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, padding: '14px 18px' },
  statLabel: { fontSize: 12, color: '#64748b', fontWeight: 500, marginBottom: 4 },
  statValue: { fontSize: 22, fontWeight: 700, color: '#0f172a' },
  statSub: { fontSize: 12, color: '#94a3b8', marginTop: 2 },

  filterRow: {
    display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
    background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10,
    padding: '12px 16px', marginBottom: 16,
  },
  filterLabel: { fontSize: 12, fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' },
  select: {
    padding: '6px 10px', border: '1px solid #e2e8f0', borderRadius: 7,
    fontSize: 13, color: '#374151', background: '#fff', cursor: 'pointer',
  },
  orderBtn: (active) => ({
    padding: '6px 10px', border: '1px solid #e2e8f0', borderRadius: 7, fontSize: 13,
    cursor: 'pointer', fontWeight: active ? 600 : 400,
    background: active ? '#f1f5f9' : '#fff', color: '#374151',
  }),
  refreshBtn: {
    padding: '6px 14px', background: '#f1f5f9', color: '#374151',
    border: 'none', borderRadius: 7, fontSize: 13, cursor: 'pointer', fontWeight: 500,
    marginLeft: 'auto',
  },
  msg: (ok) => ({ fontSize: 13, fontWeight: 500, color: ok ? '#16a34a' : '#b91c1c' }),

  tableWrap: { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, overflow: 'hidden' },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: {
    fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase',
    letterSpacing: '0.06em', padding: '10px 14px', textAlign: 'left',
    background: '#f8fafc', borderBottom: '1px solid #e2e8f0',
  },
  td: { fontSize: 13, color: '#374151', padding: '11px 14px', borderBottom: '1px solid #f1f5f9', verticalAlign: 'middle' },
  trBase: { cursor: 'pointer', transition: 'background 0.1s' },

  matchBadge: (count) => ({
    fontSize: 12, fontWeight: 700, padding: '2px 9px', borderRadius: 12, display: 'inline-block',
    background: count === 0 ? '#fee2e2' : count <= 2 ? '#fef9c3' : '#dcfce7',
    color: count === 0 ? '#b91c1c' : count <= 2 ? '#854d0e' : '#15803d',
  }),
  scoreBadge: (score) => ({
    fontSize: 12, fontWeight: 700, padding: '2px 9px', borderRadius: 12, display: 'inline-block',
    background: score >= 70 ? '#dcfce7' : score >= 50 ? '#fef9c3' : '#f1f5f9',
    color: score >= 70 ? '#15803d' : score >= 50 ? '#854d0e' : '#64748b',
  }),

  pagination: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '12px 16px', borderTop: '1px solid #e2e8f0', fontSize: 13, color: '#64748b',
  },
  pageBtn: (disabled) => ({
    padding: '5px 12px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 13,
    cursor: disabled ? 'not-allowed' : 'pointer',
    background: disabled ? '#f8fafc' : '#fff', color: disabled ? '#cbd5e1' : '#374151',
  }),
  empty: { padding: '48px', textAlign: 'center', color: '#94a3b8', fontSize: 14 },
};

export default function AdminTenders() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hovered, setHovered] = useState(null);
  const [country, setCountry] = useState('');
  const [matchStatus, setMatchStatus] = useState('');
  const [sort, setSort] = useState('created_at');
  const [order, setOrder] = useState('desc');
  const [offset, setOffset] = useState(0);
  const [msg, setMsg] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams({ sort, order, limit: LIMIT, offset });
    if (country) params.set('country', country);
    if (matchStatus) params.set('match_status', matchStatus);
    apiFetch(`/admin/tenders?${params}`)
      .then((d) => setData(d))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [sort, order, country, matchStatus, offset]);

  // Reset to page 0 when filters/sort change (but not offset itself)
  useEffect(() => { setOffset(0); }, [sort, order, country, matchStatus]); // eslint-disable-line
  useEffect(() => { load(); }, [load]);

  const tenders = data?.tenders || [];
  const stats = data?.stats || {};
  const total = data?.total || 0;
  const matchPct = stats.total ? Math.round((stats.matched / stats.total) * 100) : 0;

  return (
    <div style={s.page}>
      <h1 style={s.heading}>Tenders</h1>
      <p style={s.sub}>All tenders in the database — match coverage, scores, and re-ranking controls</p>

      {/* Stats bar */}
      <div style={s.statsBar}>
        <div style={s.statCard}>
          <div style={s.statLabel}>Total tenders</div>
          <div style={s.statValue}>{(stats.total || 0).toLocaleString()}</div>
        </div>
        <div style={s.statCard}>
          <div style={s.statLabel}>Matched</div>
          <div style={{ ...s.statValue, color: '#15803d' }}>{(stats.matched || 0).toLocaleString()}</div>
          <div style={s.statSub}>{matchPct}% coverage</div>
        </div>
        <div style={s.statCard}>
          <div style={s.statLabel}>No matches</div>
          <div style={{ ...s.statValue, color: (stats.unmatched || 0) > 0 ? '#b91c1c' : '#0f172a' }}>
            {(stats.unmatched || 0).toLocaleString()}
          </div>
        </div>
        <div style={s.statCard}>
          <div style={s.statLabel}>Deadline passed</div>
          <div style={{ ...s.statValue, color: '#64748b' }}>{(stats.expired || 0).toLocaleString()}</div>
        </div>
      </div>

      {/* Filter row */}
      <div style={s.filterRow}>
        <span style={s.filterLabel}>Filter</span>
        <select value={country} onChange={(e) => setCountry(e.target.value)} style={s.select}>
          <option value="">All countries</option>
          {COUNTRY_ISO.map((c) => (
            <option key={c.code} value={c.code}>{c.name}</option>
          ))}
        </select>
        <select value={matchStatus} onChange={(e) => setMatchStatus(e.target.value)} style={s.select}>
          <option value="">All tenders</option>
          <option value="matched">Has matches</option>
          <option value="unmatched">No matches</option>
        </select>

        <span style={{ ...s.filterLabel, marginLeft: 8 }}>Sort</span>
        <select value={sort} onChange={(e) => setSort(e.target.value)} style={s.select}>
          <option value="created_at">Ingestion date</option>
          <option value="deadline">Deadline</option>
          <option value="match_count">Match count</option>
          <option value="avg_score">Avg score</option>
          <option value="contract_value">Contract value</option>
        </select>
        <button style={s.orderBtn(order === 'desc')} onClick={() => setOrder('desc')}>↓ Desc</button>
        <button style={s.orderBtn(order === 'asc')} onClick={() => setOrder('asc')}>↑ Asc</button>

        <button style={s.refreshBtn} onClick={load}>Refresh</button>
        {msg && <div style={{ width: '100%', marginTop: 4 }}><span style={s.msg(msg.ok)}>{msg.text}</span></div>}
      </div>

      {/* Table */}
      {loading ? (
        <p style={{ color: '#94a3b8', fontSize: 14 }}>Loading…</p>
      ) : (
        <div style={s.tableWrap}>
          <table style={s.table}>
            <thead>
              <tr>
                <th style={s.th}>Title</th>
                <th style={s.th}>Country</th>
                <th style={s.th}>CPV</th>
                <th style={s.th}>Ingested</th>
                <th style={s.th}>Deadline</th>
                <th style={s.th}>Value</th>
                <th style={s.th}>Matches</th>
                <th style={s.th}>Avg score</th>
              </tr>
            </thead>
            <tbody>
              {tenders.length === 0 ? (
                <tr>
                  <td colSpan={8} style={s.empty}>No tenders found.</td>
                </tr>
              ) : tenders.map((t) => {
                const expired = isExpired(t.deadline);
                return (
                  <tr
                    key={t.id}
                    style={{ ...s.trBase, background: hovered === t.id ? '#f8fafc' : 'transparent' }}
                    onMouseEnter={() => setHovered(t.id)}
                    onMouseLeave={() => setHovered(null)}
                    onClick={() => navigate(`/admin/tenders/${t.id}`)}
                  >
                    <td style={{ ...s.td, maxWidth: 320 }}>
                      <div style={{ fontWeight: 500, color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 300 }}>
                        {t.title || '—'}
                      </div>
                      {t.ted_id && (
                        <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2, fontFamily: 'monospace' }}>{t.ted_id}</div>
                      )}
                    </td>
                    <td style={{ ...s.td, fontWeight: 500 }}>{t.country || '—'}</td>
                    <td style={{ ...s.td, fontSize: 12, color: '#64748b', fontFamily: 'monospace' }}>
                      {t.cpv_codes?.length > 0 ? (
                        <>
                          <span>{t.cpv_codes[0]}</span>
                          {t.cpv_codes.length > 1 && (
                            <span style={{ color: '#94a3b8' }}> +{t.cpv_codes.length - 1}</span>
                          )}
                        </>
                      ) : '—'}
                    </td>
                    <td style={{ ...s.td, fontSize: 12, color: '#64748b', whiteSpace: 'nowrap' }}>
                      {formatDate(t.created_at)}
                    </td>
                    <td style={{ ...s.td, whiteSpace: 'nowrap' }}>
                      <span style={{ fontSize: 13, color: expired ? '#b91c1c' : '#374151', fontWeight: expired ? 600 : 400 }}>
                        {formatDate(t.deadline)}{expired ? ' ✕' : ''}
                      </span>
                    </td>
                    <td style={{ ...s.td, fontSize: 12, whiteSpace: 'nowrap' }}>
                      {formatValue(t.contract_value, t.contract_size_band)}
                    </td>
                    <td style={s.td}>
                      <span style={s.matchBadge(t.match_count)}>{t.match_count}</span>
                    </td>
                    <td style={s.td}>
                      {t.avg_score != null ? (
                        <span style={s.scoreBadge(t.avg_score)}>{t.avg_score}</span>
                      ) : (
                        <span style={{ color: '#cbd5e1', fontSize: 12 }}>—</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {/* Pagination */}
          <div style={s.pagination}>
            <span>
              {total === 0
                ? 'No results'
                : `${offset + 1}–${Math.min(offset + LIMIT, total)} of ${total.toLocaleString()} tenders`}
            </span>
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                style={s.pageBtn(offset === 0)}
                disabled={offset === 0}
                onClick={() => setOffset(Math.max(0, offset - LIMIT))}
              >
                ← Prev
              </button>
              <button
                style={s.pageBtn(offset + LIMIT >= total)}
                disabled={offset + LIMIT >= total}
                onClick={() => setOffset(offset + LIMIT)}
              >
                Next →
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
